import { Tab } from '@mui/material';
import { AxiosError } from 'axios';
import React, { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { banksApi, binsApi } from 'api';
import {
  CloseFormikDialogResult,
  PageHeader,
  QueryTabPanel,
  QueryTabs,
} from 'components';
import { QueryKey } from 'enums';
import { useMutation, useQueryTab } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Bank, BankDto, Bin, BinDto } from 'types';
import { validationUtils } from 'utils';

import { BankBinDetailsDialog, BankBinsList } from './BankBinsList';
import { BankDetailsDialog, BanksList } from './BanksList';

enum BankPageTabs {
  Banks = 'bank',
  Bins = 'bin',
}

export const BanksPage: React.FC = () => {
  const queryClient = useQueryClient();

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.banks',
  });

  const { tab } = useQueryTab();

  const [createBankDialogOpen, setCreateBankDialogOpen] = useState(false);

  const [createBankBinDialogOpen, setCreateBankBinDialogOpen] = useState(false);

  const { mutate: createBank } = useMutation<Bank, AxiosError, BankDto>(
    banksApi.create,
  );

  const { mutate: createBankBin } = useMutation<Bin, AxiosError, BinDto>(
    binsApi.create,
  );

  const handleCreateClick = useCallback(() => {
    switch (tab) {
      case BankPageTabs.Banks:
        return setCreateBankDialogOpen(true);
      case BankPageTabs.Bins:
        return setCreateBankBinDialogOpen(true);
    }
  }, [tab]);

  const handleCreateBankDialogClose = useCallback(
    ({ data, ok }: CloseFormikDialogResult<BankDto>) => {
      if (ok && data) {
        return createBank(data.values, {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.BanksArchive);
            queryClient.invalidateQueries(QueryKey.BanksNotArchive);
            queryClient.invalidateQueries(QueryKey.Banks);
            data.formikHelpers.resetForm();
            setCreateBankDialogOpen(false);
          },
          onError: (error: AxiosError) => {
            data.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        });
      }
      setCreateBankDialogOpen(false);
    },
    [queryClient, createBank],
  );

  const handleCreateBankBinDialogClose = useCallback(
    ({ data, ok }: CloseFormikDialogResult<BinDto>) => {
      if (ok && data) {
        return createBankBin(data.values, {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.Bins);
            data.formikHelpers.resetForm();
            setCreateBankBinDialogOpen(false);
          },
          onError: (error: AxiosError) => {
            data.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        });
      }
      setCreateBankBinDialogOpen(false);
    },
    [queryClient, createBankBin],
  );

  return (
    <Fragment>
      <PageHeader
        title={t('title')}
        rightContentButton={{
          onClick: handleCreateClick,
        }}
        divider={false}
      />
      <QueryTabs tabsEnum={BankPageTabs}>
        <Tab value={BankPageTabs.Banks} label={t('tabs.banks')} wrapped />
        <Tab value={BankPageTabs.Bins} label={t('tabs.bins')} wrapped />
      </QueryTabs>
      <QueryTabPanel value={BankPageTabs.Banks}>
        <BanksList />
      </QueryTabPanel>
      <QueryTabPanel value={BankPageTabs.Bins}>
        <BankBinsList />
      </QueryTabPanel>
      <BankDetailsDialog
        open={createBankDialogOpen}
        onClose={handleCreateBankDialogClose}
      />
      <BankBinDetailsDialog
        open={createBankBinDialogOpen}
        onClose={handleCreateBankBinDialogClose}
      />
    </Fragment>
  );
};
